import React from 'react';
import {Conversion} from '../../domain/types';
import {formatRelativeDate, formatExactDate} from '../../lib/formatDate';
import {stripTimestamp} from '../../utils/filenameUtils';
import {AlertCircleIcon, DownloadIcon, FileCodeIcon, ListIcon, RefreshIcon, TrashIcon} from '../Icons';

export interface ConversionRowActions {
  onDownload: (conversion: Conversion) => void;
  onDelete: (conversion: Conversion) => void;
  /** Re-run this conversion so the XML it produces names its source audio. */
  onRegenerate: (conversion: Conversion) => void;
}

interface ConversionRowProps extends ConversionRowActions {
  conversion: Conversion;
  isDownloading: boolean;
  /**
   * Set when this conversion predates the recorded media reference. The value is why it cannot
   * simply be run again, or `null` when it can — see `describeRegenerateBlock`.
   */
  mediaNotice: {blockedReason: string | null} | null;
  isRegenerating: boolean;
}

/** Both paths are R2 keys; the user only ever wanted the filename they asked for. */
export function conversionFileName(conversion: Conversion): string {
  const key = conversion.xmlR2Key || conversion.xmlPath || '';
  return stripTimestamp(key.split(/[\\/]/).pop() || 'conversion.xml');
}

/** The stored marker mode, back in the words the conversion dialog asked the question in. */
export function markerLabel(markers: string): string {
  if (markers === 'with censored word' || markers === 'with-word') {
    return 'Markers + word';
  }
  return markers === 'clean' ? 'Markers' : 'No markers';
}

/**
 * The word lists a conversion was made from, as a label and a full-text title.
 *
 * Reads the snapshotted `wordListNames` rather than resolving `wordListIds` against the lists that
 * exist now: a list can be renamed or deleted after the fact, and the snapshot is the only record
 * of what this XML actually censored. Rows made before conversions took a set carry one name, and
 * pre-multi-list rows fall back to the deprecated singular field in the thunk's mapping, so this
 * needs no special case for them.
 */
export function wordListSummary(conversion: Conversion): {label: string; title: string} | null {
  const names = conversion.wordListNames;

  if (names.length === 0) {
    return null;
  }

  return {
    // One name in the column, the rest as a count — a set of five would otherwise push everything
    // else out of the row, and the full list is a hover away.
    label: names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`,
    title: names.join(', '),
  };
}

const chip = 'inline-flex items-center rounded bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-400';

/**
 * One finished XML.
 *
 * A conversion is a record of a decision — which word list, cut or beep, markers or not — plus the
 * file it produced, so the row states the decision and keeps Download as the only thing worth
 * reaching for. The desktop-era card repeated both R2 keys in full and buried the button beside them.
 */
const ConversionRow: React.FC<ConversionRowProps> = ({
  conversion,
  isDownloading,
  mediaNotice,
  isRegenerating,
  onDownload,
  onDelete,
  onRegenerate,
}) => {
  const fileName = conversionFileName(conversion);
  const wordLists = wordListSummary(conversion);
  const sourceCsv = conversion.csvPath.split(/[\\/]/).pop() || '';

  return (
    <div className="group grid grid-cols-12 items-center gap-4 px-4 py-3 border-b border-gray-800/70
     last:border-b-0 hover:bg-gray-800/30 transition-colors">
      {/* File */}
      <div className="col-span-12 md:col-span-4 flex items-start gap-3 min-w-0">
        <FileCodeIcon className="w-4 h-4 mt-0.5 shrink-0 text-gray-600" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-100 truncate" title={fileName}>{fileName}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className={chip}>{conversion.mode === 'beep' ? 'Beep' : 'Silence'}</span>
            <span className={chip}>{markerLabel(conversion.markers)}</span>
          </div>
          {sourceCsv && (
            <p className="mt-1 text-xs text-gray-600 truncate" title={conversion.csvPath}>
              from {stripTimestamp(sourceCsv)}
            </p>
          )}
        </div>
      </div>

      {/* Word lists */}
      <div className="col-span-6 md:col-span-2 min-w-0">
        {wordLists ? (
          <span className="flex items-center gap-1.5 min-w-0 text-xs text-gray-300">
            <ListIcon className="w-3.5 h-3.5 shrink-0 text-gray-600" />
            <span className="truncate" title={wordLists.title}>{wordLists.label}</span>
          </span>
        ) : (
          <span className="text-xs text-gray-600">—</span>
        )}
      </div>

      {/* Censored words */}
      <div className="col-span-3 md:col-span-1 text-xs tabular-nums text-gray-300">
        <span className="md:hidden text-gray-500">Censored </span>
        {conversion.censoredWords}
      </div>

      {/* Cuts */}
      <div className="col-span-3 md:col-span-1 text-xs tabular-nums text-gray-300">
        <span className="md:hidden text-gray-500">Cuts </span>
        {conversion.totalCuts}
      </div>

      {/* Created */}
      <div className="col-span-6 md:col-span-2 text-xs text-gray-400" title={formatExactDate(conversion.createdAt)}>
        {formatRelativeDate(conversion.createdAt)}
      </div>

      {/* Actions */}
      <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => onDownload(conversion)}
          disabled={isDownloading}
          title={`Download ${fileName}`}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs
           font-medium text-white hover:bg-indigo-500 transition-colors
           disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          <DownloadIcon className="w-3.5 h-3.5" />
          {isDownloading ? 'Downloading…' : 'Download'}
        </button>
        <button
          type="button"
          onClick={() => onDelete(conversion)}
          aria-label={`Delete ${fileName}`}
          title="Delete this conversion record"
          className="p-1.5 rounded-lg text-gray-600 hover:text-red-300 hover:bg-gray-800 transition-colors
           opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>

      {mediaNotice && (
        <div className="col-span-12 flex flex-wrap items-start gap-x-2 gap-y-1.5 rounded-lg
         border border-gray-800 bg-gray-950/50 px-2.5 py-2 text-xs text-gray-500">
          <AlertCircleIcon className="mt-px w-3.5 h-3.5 shrink-0 text-amber-500/70" />
          <p className="min-w-0 flex-1">
            Generated before we started recording which audio file this points at. If your editor
            can&rsquo;t link the audio when you import it, convert it again.
            {mediaNotice.blockedReason && (
              <span className="text-gray-600"> {mediaNotice.blockedReason}</span>
            )}
          </p>
          {!mediaNotice.blockedReason && (
            <button
              type="button"
              onClick={() => onRegenerate(conversion)}
              disabled={isRegenerating}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-800
               px-2 py-1 font-medium text-gray-300 hover:border-gray-700 hover:text-gray-100
               hover:bg-gray-800/60 transition-colors disabled:opacity-40
               disabled:cursor-not-allowed cursor-pointer"
            >
              <RefreshIcon className={`w-3.5 h-3.5 ${isRegenerating ? 'animate-spin' : ''}`} />
              {isRegenerating ? 'Converting…' : 'Convert again'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversionRow;
